import { useEffect, useMemo } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';

import { Listings, SagisConfig } from 'containers/AppContainer/typings';

import {
  getRisks,
  regulationInfoValidator,
  checkJudgementOnMeritsValidation,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import RegulationInformationByRisk from 'containers/CourtManagerLegalProceedings/Provision/Regulation/RegulationInformationByRisk';
import AdditionalWarrentiesInformation from 'containers/CourtManagerLegalProceedings/Provision/Regulation/AdditionalWarrantiesInformation';
import { validateRisk } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/validator';
import { RISK } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { REGULATIONS } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { ClaimRecapType } from 'typings/claimTypes';

export interface RegulationInformationProps {
  intl: IntlShape;
  sagisClaim: any;
  claimRecap: ClaimRecapType;
  setRiskRecap: Function;
  riskRecap: any;
  listings: Listings;
  judgementOnMeritsByVictims: any;
  requestGetJudgementOnMeritsByVictims: Function;
  judgementOnMeritsByVehicles: any;
  requestGetJudgementOnMeritsByVehicles: Function;
  setIsRegulationValidated: Function;
  activeStep: number;
  isRegulationValidated: any;
  riskData: any;
  isStepValidated: any;
  setMessageError: Function;
  messageError: any;
  setIsStepValidated: Function;
  substituteDocumentNature: SagisConfig[];
  setSubstituteDocumentNature: Function;
  isJomValidated: any;
  setIsJomValidated: Function;
  requestGetAssignedLawyers: Function;
  judgementOnMeritsWithCivjvm: any;
  isBasicManagementRegulation?: boolean;
  judgementNatureProcedureIds: any;
}

export const RegulationInformation: React.FC<RegulationInformationProps> = ({
  sagisClaim,
  claimRecap,
  listings,
  judgementOnMeritsByVictims,
  requestGetJudgementOnMeritsByVictims,
  requestGetJudgementOnMeritsByVehicles,
  judgementOnMeritsByVehicles,
  setIsRegulationValidated,
  isRegulationValidated,
  activeStep,
  riskData,
  isStepValidated,
  setMessageError,
  messageError,
  setIsStepValidated,
  substituteDocumentNature,
  setSubstituteDocumentNature,
  ...props
}): JSX.Element => {
  const {
    rcCorpo = {},
    rcMaterial = {},
    additionalWarrenties = {},
  } = useMemo(() => getRisks(props.riskRecap), [props.riskRecap]);
  useEffect(() => {
    !isEmpty(rcCorpo) &&
      checkJudgementOnMeritsValidation(
        props.riskRecap,
        rcCorpo,
        judgementOnMeritsByVictims,
        props.isJomValidated,
        props.setIsJomValidated,
        props.isBasicManagementRegulation,
      );
    !isEmpty(rcMaterial) &&
      checkJudgementOnMeritsValidation(
        props.riskRecap,
        rcMaterial,
        judgementOnMeritsByVehicles,
        props.isJomValidated,
        props.setIsJomValidated,
        props.isBasicManagementRegulation,
      );
  }, [
    rcCorpo,
    rcMaterial,
    get(rcCorpo.regulationInfo, REGULATIONS.JUDGEMENT_DATE),
    get(rcCorpo.regulationInfo, REGULATIONS.JUDGEMENT_DATE_JAF),
    get(rcMaterial.regulationInfo, REGULATIONS.JUDGEMENT_DATE),
    get(rcMaterial.regulationInfo, REGULATIONS.JUDGEMENT_DATE_JAF),
    activeStep,
    judgementOnMeritsByVehicles,
  ]);

  useEffect(() => {
    regulationInfoValidator(
      props.riskRecap,
      judgementOnMeritsByVictims,
      judgementOnMeritsByVehicles,
      setIsRegulationValidated,
      isRegulationValidated,
      activeStep,
      translate(messages.judgementOnMeritsVehiclesNotFound, props),
      translate(messages.judgementOnMeritsVictimsNotFound, props),
      props.isJomValidated,
      props.isBasicManagementRegulation,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.riskRecap,
    props.isJomValidated[RISK.CORPOREAL],
    props.isJomValidated[RISK.MATERIAL],
    judgementOnMeritsByVictims,
    judgementOnMeritsByVehicles,
  ]);

  const errorByRisk = Object.values({ ...messageError }).filter(
    item => !isEmpty(item),
  );
  useEffect(() => {
    const corpoMessage = validateRisk(rcCorpo, props.intl.formatMessage);
    const materialMessage = validateRisk(rcMaterial, props.intl.formatMessage);
    const additionalWarrentiesMessage = validateRisk(
      additionalWarrenties,
      props.intl.formatMessage,
    );
    setMessageError({
      [get(rcCorpo, 'regulationId', null)]: corpoMessage,
      [get(rcMaterial, 'regulationId', '')]: materialMessage,
      [get(
        additionalWarrenties,
        'regulationId',
        null,
      )]: additionalWarrentiesMessage,
    });
  }, [props.riskRecap]);

  useEffect(() => {
    isEmpty(errorByRisk) &&
      setIsStepValidated({ ...isStepValidated, [activeStep]: false });
  }, [messageError]);
  return (
    <main>
      {!isEmpty(rcCorpo) && (
        <RegulationInformationByRisk
          risk={rcCorpo}
          requestGetJudgementOnMerits={requestGetJudgementOnMeritsByVictims}
          sagisClaim={sagisClaim}
          claimRecap={claimRecap}
          setRiskRecap={props.setRiskRecap}
          riskRecap={props.riskRecap}
          listings={listings}
          activeStep={activeStep}
          isRegulationValidated={isRegulationValidated}
          judgementOnMerits={judgementOnMeritsByVictims}
          substituteDocumentNature={substituteDocumentNature}
          setSubstituteDocumentNature={setSubstituteDocumentNature}
          riskData={riskData}
          isStepValidated={isStepValidated}
          setMessageError={setMessageError}
          messageError={messageError}
          setIsStepValidated={setIsStepValidated}
          isJomValidated={props.isJomValidated}
          requestGetAssignedLawyers={props.requestGetAssignedLawyers}
          judgementOnMeritsWithCivjvm={props.judgementOnMeritsWithCivjvm}
          isBasicManagementRegulation={props.isBasicManagementRegulation}
          judgementNatureProcedureIds={props.judgementNatureProcedureIds}
        />
      )}
      {!isEmpty(rcMaterial) && (
        <RegulationInformationByRisk
          risk={rcMaterial}
          requestGetJudgementOnMerits={requestGetJudgementOnMeritsByVehicles}
          sagisClaim={sagisClaim}
          claimRecap={claimRecap}
          setRiskRecap={props.setRiskRecap}
          riskRecap={props.riskRecap}
          listings={listings}
          activeStep={activeStep}
          isRegulationValidated={isRegulationValidated}
          judgementOnMerits={judgementOnMeritsByVehicles}
          substituteDocumentNature={substituteDocumentNature}
          setSubstituteDocumentNature={setSubstituteDocumentNature}
          riskData={riskData}
          isStepValidated={isStepValidated}
          setMessageError={setMessageError}
          messageError={messageError}
          setIsStepValidated={setIsStepValidated}
          isJomValidated={props.isJomValidated}
          requestGetAssignedLawyers={props.requestGetAssignedLawyers}
          judgementOnMeritsWithCivjvm={[]}
          judgementNatureProcedureIds={props.judgementNatureProcedureIds}
        />
      )}
      <AdditionalWarrentiesInformation
        risk={additionalWarrenties}
        sagisClaim={sagisClaim}
        claimRecap={claimRecap}
        setRiskRecap={props.setRiskRecap}
        riskRecap={props.riskRecap}
        listings={listings}
        isStepValidated={isStepValidated}
        setMessageError={setMessageError}
        messageError={messageError}
        activeStep={activeStep}
      />
    </main>
  );
};

export default injectIntl(RegulationInformation);
